<script setup lang="ts">
import {LayersThree01, Users03, MessageDotsCircle, AlertTriangle, ArrowNarrowRight} from "utruckin-icons";
import {Component, ref} from "vue";
import {useSearchStore} from "@/features/search/search.store";

interface ITab {
  label: string;
  value: number;
  icon: Component
}

const searchStore = useSearchStore();

const activeTab = ref<number>(0);
const tabs = ref<ITab[]>([
  {
    value: 0,
    label: 'All',
    icon: LayersThree01
  },
  {
    value: 1,
    label: 'Users',
    icon: Users03
  },
  {
    value: 2,
    label: 'Message',
    icon: MessageDotsCircle
  },
  {
    value: 3,
    label: 'Error messages',
    icon: AlertTriangle
  },
])

const change = (tab: ITab) => {
  activeTab.value = tab.value
}
</script>

<template>
  <div class="w-ui-tab">
    <div class="tab__header">
      <div
          class="tab"
          v-for="(tab, index) in tabs"
          :key="index"
          :class="tab.value == activeTab ? 'active' : ''"
          @click="change(tab)"
      >
        <div class="tab__body">
          <div class="tab__body--icon">
            <component class="icon" :is="tab.icon" :size="16"/>
          </div>
          <div class="tab__body--label">
            {{ tab.label }}
          </div>
        </div>
      </div>
    </div>

    <div class="tab__search">
      <ul class="list__header">
        <li class="list__head">
          <div class="main__header">
            <div class="main__header--title">
              <div class="icon">
                <Users03 :size="16"/>
              </div>
              <div class="headline">Users</div>
            </div>

            <div class="main__header--action">
              <ArrowNarrowRight :size="16"/>
            </div>
          </div>

          <ul class="child__list">
            <li class="child__list--item" v-for="item in searchStore.smartSearchData?.chats">
              {{ item.title }}
            </li>
          </ul>
        </li>
        <li class="list__head">
          <div class="main__header">
            <div class="main__header--title">
              <div class="icon">
                <MessageDotsCircle :size="16"/>
              </div>
              <div class="headline">Message</div>
            </div>

            <div class="main__header--action">
              <ArrowNarrowRight :size="16"/>
            </div>
          </div>

          <ul class="child__list">
            <li class="child__list--item" v-for="item in searchStore.smartSearchData?.messages">{{ item.text }}</li>
          </ul>
        </li>
        <li class="list__head">
          <div class="main__header">
            <div class="main__header--title">
              <div class="icon">
                <AlertTriangle :size="16"/>
              </div>
              <div class="headline">Error messages</div>
            </div>

            <div class="main__header--action">
              <ArrowNarrowRight :size="16"/>
            </div>
          </div>

          <ul class="child__list">
            <li class="child__list--item" v-for="item in searchStore.smartSearchData?.errors">
              {{ item.message_text_preview }}
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.w-ui-tab {
  .tab__header {
    margin-top: 8px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    border-radius: 14px;
    display: flex;
    justify-content: space-between;
    padding: 4px;

    .tab {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 6px 16px;
      border-radius: 11px;
      cursor: pointer;
      background: transparent;
      color: var(--color-label);

      &:not(:last-child) {
        margin-right: 8px;
        position: relative;

        &::after {
          content: "";
          position: absolute;
          height: 16px;
          width: 1px;
          background: var(--color-border);
          right: -8px;
          top: calc(50% - 8px);
          cursor: default;
        }
      }

      &:not(:first-child) {
        margin-left: 8px;
      }

      &__body {
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        background: transparent;
        display: flex;
        align-items: center;
        gap: 10px;

        &--icon {
          background: transparent;
          display: flex;
          align-items: center;
        }

        &--label {
          background: transparent;
          font-size: 14px;
          line-height: 20px;
          font-weight: 500;
        }
      }

      &.active {
        background: var(--color-background);
        box-shadow: 0 0 4px 0 #A6A6A640;
      }
    }
  }

  .tab__search {
    border: 1px solid var(--color-border);
    border-radius: 16px;
    overflow: hidden;
    margin-top: 8px;

    ul, li, li > * {
      color: var(--color-label);
    }

    .list__header {
      .list__head {
        &:not(:first-child) {
          border-top: 1px solid var(--color-border);
        }
      }
    }

    .main__header {
      background: #DEDEDE33;
      padding: 11px 15px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;

      &--title {
        background: transparent;
        display: flex;
        gap: 8px;

        .icon {
          background: transparent;
        }

        .headline {
          background: transparent;
          font-weight: 500;
          font-size: 14px;
          line-height: 20px;
        }
      }

      &--action {
        background: transparent;
        color: var(--color-secondary-label);
      }
    }

    .child__list {
      &--item {
        padding: 12px;
        border-top: 1px solid var(--color-border);
        display: -webkit-box !important;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}
</style>
